import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { CARD } from './ui';
import { formatSubmittedAt } from './dateFormat';
import { API_BASE_URL } from './config';
import { DEFAULT_RANGE_ID, RANGE_OPTIONS, filterByRange, findRange } from './submissionRange';

const MySubmissions = () => {
  const navigate = useNavigate();
  const [submissions, setSubmissions] = useState([]);
  const [rangeId, setRangeId] = useState(DEFAULT_RANGE_ID);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    const fetchMine = async () => {
      try {
        const res = await axios.get(`${API_BASE_URL}/api/submissions?mine=1`);
        if (!cancelled) setSubmissions(res.data);
      } catch (err) {
        console.error('Failed to fetch your submissions:', err);
        if (!cancelled) setError('Failed to load your submissions.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetchMine();
    return () => {
      cancelled = true;
    };
  }, []);

  const range = findRange(rangeId);
  const visible = useMemo(() => filterByRange(submissions, range.days), [submissions, range.days]);

  // App.css carries a global `button:hover { background-color: #0056b3 }` whose
  // pseudo-class outranks a lone Tailwind `bg-*`, so both states are spelled out.
  const tabClass = (active) =>
    `px-3 py-1.5 rounded-lg text-xs font-medium transition-colors duration-200 border ${
      active
        ? 'bg-purple-500/20 hover:bg-purple-500/30 text-purple-200 border-purple-400/40'
        : 'bg-gray-800/60 hover:bg-gray-700/60 text-gray-400 border-gray-700 hover:text-white hover:border-gray-500'
    }`;

  return (
    <div className={`${CARD} overflow-hidden mt-8`}>
      <div className="px-6 py-5 border-b border-gray-700 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold text-white">Your Submitted Surveys</h2>
          <p className="text-sm text-gray-400 mt-0.5">
            Surveys you have submitted, newest first.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {RANGE_OPTIONS.map((option) => (
            <button
              key={option.id}
              type="button"
              onClick={() => setRangeId(option.id)}
              aria-pressed={option.id === rangeId}
              className={tabClass(option.id === rangeId)}
            >
              {option.label}
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <p className="px-6 py-10 text-center text-sm text-gray-400">Loading your submissions...</p>
      ) : error ? (
        <p className="px-6 py-10 text-center text-sm text-red-300">{error}</p>
      ) : visible.length === 0 ? (
        <p className="px-6 py-10 text-center text-sm text-gray-500">
          {submissions.length === 0
            ? "You haven't submitted any surveys yet."
            : `Nothing submitted in the last ${range.label.toLowerCase()}.`}
        </p>
      ) : (
        <ul className="divide-y divide-gray-700/60">
          {visible.map((submission) => (
            <li key={submission.id}>
              <button
                type="button"
                onClick={() => navigate(`/submitted/${submission.id}`)}
                className="w-full text-left bg-transparent px-6 py-4 hover:bg-gray-700/30 transition-colors duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-purple-400"
              >
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-base font-medium text-white truncate">
                    {submission.title || submission.form_name || `Submission #${submission.id}`}
                  </span>
                  {submission.parent_submission_id && (
                    <span className="text-xs px-2 py-0.5 rounded-full bg-amber-500/15 text-amber-300 border border-amber-500/30">
                      Re-submission
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-2 mt-0.5 text-xs text-gray-500 flex-wrap">
                  {submission.form_name && submission.title !== submission.form_name && (
                    <span>{submission.form_name}</span>
                  )}
                  <span>
                    Submitted {formatSubmittedAt(submission.submitted_at, submission.submitted_tz)}
                  </span>
                </div>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default MySubmissions;
